import { Link } from "react-router-dom";
import { Mail, MapPin, Linkedin } from "lucide-react";
import AppStoreBadges from "./AppStoreBadges";
import { useLanguage } from "@/contexts/LanguageContext";
import { t } from "@/utils/translations";
import { trackEvent, trackFooterDemoClick } from "@/utils/analytics";

const CALENDLY_URL_PLACEHOLDER = "/book-demo";

const Footer = () => {
  const { language, isRTL, getAlternatePath } = useLanguage();

  const handleDemoClick = () => {
    trackFooterDemoClick();
    window.location.href = CALENDLY_URL_PLACEHOLDER;
  };

  return (
    <footer className="bg-[#1e1e3c] text-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 mb-12">
          {/* Company Info */}
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <div className="flex items-center space-x-3 rtl:space-x-reverse mb-6">
              <div className="bg-gradient-to-r from-flip-brand to-pink-500 w-10 h-10 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-lg">F</span>
              </div>
              <span className="text-xl font-bold">Flip For Business</span>
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed">
              {t('cryptoPaymentsSimple', language)}
            </p>

            <div className="flex items-center space-x-3 rtl:space-x-reverse mb-6">
              <a href="https://www.linkedin.com/company/flip-company-as/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Flip on LinkedIn">
                <Linkedin className="h-5 w-5" />
              </a>
            </div>

            <AppStoreBadges className="mb-6" />

            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <p className="text-xs text-gray-400">
                Operated by Flip Company AS — built for global business payouts.
              </p>
            </div>
          </div>

          {/* Solutions */}
          <div>
            <h3 className="font-semibold text-white mb-6">{t('solutions', language)}</h3>
            <ul className="space-y-3 text-sm">
              <li><Link to="/solutions/cross-border-payments" className="text-gray-400 hover:text-white transition-colors">{t('crossBorderPayments', language)}</Link></li>
              <li><Link to="/solutions/bulk-payments" className="text-gray-400 hover:text-white transition-colors">{t('bulkPayments', language)}</Link></li>
              <li><Link to="/solutions/freelancer-payments" className="text-gray-400 hover:text-white transition-colors">{t('freelancerPayments', language)}</Link></li>
              <li><Link to="/solutions" className="text-gray-400 hover:text-white transition-colors">{t('vendorSettlements', language)}</Link></li>
            </ul>
          </div>

          {/* Industries */}
          <div>
            <h3 className="font-semibold text-white mb-6">{t('industries', language)}</h3>
            <ul className="space-y-3 text-sm">
              <li><Link to="/industries/creative-studios" className="text-gray-400 hover:text-white transition-colors">{language === 'ar' ? 'الوكالات' : 'Agencies'}</Link></li>
              <li><Link to="/industries/e-commerce" className="text-gray-400 hover:text-white transition-colors">{t('ecommerce', language)}</Link></li>
              <li><Link to="/enterprise" className="text-gray-400 hover:text-white transition-colors">{language === 'ar' ? 'المؤسسات' : 'Enterprise'}</Link></li>
              <li><Link to="/eor-payroll-platforms" className="text-gray-400 hover:text-white transition-colors">{language === 'ar' ? 'منصات EOR' : 'EOR Platforms'}</Link></li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="font-semibold text-white mb-6">{t('resources', language)}</h3>
            <ul className="space-y-3 text-sm">
              <li><Link to="/blog" className="text-gray-400 hover:text-white transition-colors">{t('blog', language)}</Link></li>
              <li><Link to="/resources" className="text-gray-400 hover:text-white transition-colors">{language === 'ar' ? 'أدلة' : 'Guides'}</Link></li>
              <li><Link to="/tools" className="text-gray-400 hover:text-white transition-colors">{language === 'ar' ? 'حاسبات العملات' : 'Currency Converters'}</Link></li>
              <li><Link to="/uae/resources" className="text-gray-400 hover:text-white transition-colors">{language === 'ar' ? 'موارد الإمارات' : 'UAE Resources'}</Link></li>
              <li><Link to="/europe" className="text-gray-400 hover:text-white transition-colors">{language === 'ar' ? 'أوروبا' : 'Europe Payouts'}</Link></li>
              <li><Link to="/pricing" className="text-gray-400 hover:text-white transition-colors">{t('pricing', language)}</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="font-semibold text-white mb-6">{t('company', language)}</h3>
            <ul className="space-y-3 text-sm">
              <li><Link to="/about" className="text-gray-400 hover:text-white transition-colors">{t('about', language)}</Link></li>
              <li>
                <Link
                  to="/contact"
                  onClick={() => trackEvent('footer_contact_click', { target: 'contact_page' })}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  {t('contactUs', language)}
                </Link>
              </li>
              <li><Link to="/faq" className="text-gray-400 hover:text-white transition-colors">{t('faqSupport', language)}</Link></li>
              <li>
                <div className="flex items-center space-x-2 rtl:space-x-reverse mt-1">
                  <Mail className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                  <a
                    href="mailto:business@theflip.app"
                    onClick={() => trackEvent('footer_contact_click', { target: 'email', email: 'business@theflip.app' })}
                    className="text-gray-400 hover:text-white transition-colors underline underline-offset-4 decoration-gray-600 hover:decoration-white"
                  >
                    business@theflip.app
                  </a>
                </div>
              </li>
              <li>
                <div className="flex items-start space-x-2 rtl:space-x-reverse">
                  <MapPin className="h-3.5 w-3.5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-gray-400">{t('globalOperations', language)}</span>
                </div>
              </li>
            </ul>
          </div>

          {/* Talk to us */}
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <h3 className="font-semibold text-white mb-6">{language === 'ar' ? 'تحدث إلينا' : 'Talk to us'}</h3>
            <p className="text-sm text-gray-400 mb-4">
              {language === 'ar' ? 'احصل على عرض شخصي لسير عمل الدفع الخاص بك.' : 'Get a personalized walkthrough of your payout workflow.'}
            </p>
            <button
              onClick={handleDemoClick}
              className="btn-flip-primary h-12 px-6 text-sm rounded-xl w-full sm:w-auto"
            >
              {language === 'ar' ? 'احجز عرضاً توضيحياً' : 'Book a Demo'}
            </button>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-gray-700 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-sm text-gray-400">
              © 2026 Flip Company AS. {t('allRightsReserved', language)}
            </div>
            <div className="flex flex-wrap items-center gap-4 sm:gap-8">
              <Link to="/privacy" className="text-sm text-gray-400 hover:text-white transition-colors">{t('privacyPolicy', language)}</Link>
              <Link to="/terms" className="text-sm text-gray-400 hover:text-white transition-colors">{t('termsOfService', language)}</Link>
              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                <span className="text-sm text-gray-400">{t('language', language)}</span>
                <Link
                  to={getAlternatePath()}
                  onClick={() => trackEvent('footer_language_click', { language })}
                  className="text-sm text-primary hover:text-pink-400 transition-colors"
                >
                  {language === 'en' ? 'English' : 'العربية'}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
